// src/components/settings/KennelSettings.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  Table,
  TableRow,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import {
  kennelsAPI,
  referenceDataAPI,
  KennelSide,
  Kennel,
  KennelSize,
  KennelReferenceUsage,
  PetType,
} from '../../services/api';
import { useErrorHandler } from '../../errors';
import { useGlobalToast } from '../../App';
import SettingsHeaderRow from './shared/SettingsHeaderRow';
import {
  ConfirmDeleteDialog,
  RefCountChip,
  KennelCountChip,
  SystemChip,
} from './shared/SettingsElements';
import SettingsTableActions from './shared/SettingsTableActions';
import {
  AccordionButtonLabel,
  StyledTableContainer,
  StyledTableBody,
  StyledTableRow,
  StyledTableCell,
  StyledTableHead,
  StyledTableHeaderCell,
  StyledTableChipCell,
  StyledTableChipCellContent,
  StyledTableActionsCell,
} from './shared/SettingsTableElements';
import {
  StyledAccordion,
  StyledAccordionSummary,
  StyledAccordionDetails,
} from './shared/SettingsAccordionElements';
import { AccountActionButton as SettingsActionButton } from '../common/Buttons';
import KennelSideDialog from './shared/dialogs/KennelSideDialog';
import KennelDialog from './shared/dialogs/KennelDialog';
import KennelSizeDialog from './shared/dialogs/KennelSizeDialog';
import LoadingSpinner from '../common/LoadingSpinner';

interface SideWithCount extends KennelSide {
  hasKennels?: boolean;
  hasHeaters?: boolean;
  kennelCount?: number;
}

const EMPTY_USAGE: KennelReferenceUsage = { kennels: {}, sides: {}, sizes: {} };

const KENNEL_SIDE_ORDER: Record<string, number> = {
  SideA: 1,
  SideB: 2,
  Cattery: 3,
  AnimalRoom: 4,
};

const ACCORDION_ICON_COLORS = {
  edit: '#fff',
  delete: '#fff',
  disabledDelete: '#fff',
};

const KennelsSettings: React.FC = () => {
  const [sides, setSides] = useState<SideWithCount[]>([]);
  const [kennels, setKennels] = useState<Kennel[]>([]);
  const [kennelSizes, setKennelSizes] = useState<KennelSize[]>([]);
  const [petTypes, setPetTypes] = useState<PetType[]>([]);
  const [usage, setUsage] = useState<KennelReferenceUsage>(EMPTY_USAGE);
  const [loading, setLoading] = useState(true);
  const [sideDialogOpen, setSideDialogOpen] = useState(false);
  const [kennelDialogOpen, setKennelDialogOpen] = useState(false);
  const [sizeDialogOpen, setSizeDialogOpen] = useState(false);
  const [editingSide, setEditingSide] = useState<SideWithCount | null>(null);
  const [editingKennel, setEditingKennel] = useState<Kennel | null>(null);
  const [editingSize, setEditingSize] = useState<KennelSize | null>(null);
  const [activeSideId, setActiveSideId] = useState(0);
  const [expandedSide, setExpandedSide] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'side' | 'kennel' | 'size';
    id: number;
    name: string;
  } | null>(null);
  const { handleError } = useErrorHandler();
  const { showSuccess } = useGlobalToast();
  const theme = useTheme();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [sidesData, kennelsData, sizesData, usageData, petTypesData] =
        await Promise.all([
          kennelsAPI.getSides(),
          kennelsAPI.getAll(),
          kennelsAPI.getSizes(),
          kennelsAPI.getReferenceUsage(),
          referenceDataAPI.getPetTypes(),
        ]);
      setSides(sidesData);
      setKennels(kennelsData);
      setKennelSizes(sizesData);
      setUsage(usageData);
      setPetTypes(petTypesData);
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- Side handlers ---
  const openSideDialog = (side?: SideWithCount) => {
    setEditingSide(side ?? null);
    setSideDialogOpen(true);
  };

  const saveSide = async (formData: {
    name: string;
    hasKennels: boolean;
    hasHeaters: boolean;
    allowedPetTypeIds: number[];
  }) => {
    try {
      if (editingSide) {
        await kennelsAPI.updateSide(editingSide.id, formData);
        showSuccess();
      } else {
        await kennelsAPI.createSide(formData);
        showSuccess();
      }
      await fetchData();
      setSideDialogOpen(false);
    } catch (e) {
      handleError(e);
    }
  };

  // --- Kennel handlers ---
  const openKennelDialog = (sideId: number, kennel?: Kennel) => {
    setActiveSideId(sideId);
    setEditingKennel(kennel ?? null);
    setKennelDialogOpen(true);
  };

  const saveKennel = async (formData: {
    kennelNumber: number;
    kennelName: string;
    kennelSizeId: number;
    kennelSideId: number;
    active?: boolean;
  }) => {
    try {
      if (editingKennel) {
        await kennelsAPI.update(editingKennel.id, formData);
        showSuccess();
      } else {
        await kennelsAPI.create(formData);
        showSuccess();
      }
      await fetchData();
      setKennelDialogOpen(false);
    } catch (e) {
      handleError(e);
    }
  };

  // --- Size handlers ---
  const openSizeDialog = (size?: KennelSize) => {
    setEditingSize(size ?? null);
    setSizeDialogOpen(true);
  };

  const saveSize = async (formData: { name: string; sortOrder: number }) => {
    try {
      if (editingSize) {
        await kennelsAPI.updateSize(editingSize.id, formData);
        showSuccess();
      } else {
        await kennelsAPI.createSize(formData);
        showSuccess();
      }
      await fetchData();
      setSizeDialogOpen(false);
    } catch (e) {
      handleError(e);
    }
  };

  // --- Delete handler ---
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === 'side') {
        await kennelsAPI.deleteSide(deleteTarget.id);
        showSuccess();
      } else if (deleteTarget.type === 'size') {
        await kennelsAPI.deleteSize(deleteTarget.id);
        showSuccess();
      } else {
        await kennelsAPI.delete(deleteTarget.id);
        showSuccess();
      }
      await fetchData();
    } catch (e) {
      handleError(e);
    } finally {
      setDeleteTarget(null);
    }
  };

  const getSortedSides = (): SideWithCount[] => {
    return [...sides].sort((a, b) => {
      const orderA = KENNEL_SIDE_ORDER[a.name] ?? 999;
      const orderB = KENNEL_SIDE_ORDER[b.name] ?? 999;
      return orderA - orderB;
    });
  };

  const handleAccordionChange =
    (sideId: number) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedSide(isExpanded ? sideId : null);
    };

  if (loading) {
    return <LoadingSpinner message="Loading Kennels..." size={80} />;
  }

  return (
    <Box>
      {/* Kennel Sizes Section */}
      <SettingsHeaderRow
        title="Manage Kennel Sizes"
        onAdd={() => openSizeDialog()}
        addLabel="Add Kennel Size"
        buttonMinWidth="320px"
      />

      {kennelSizes.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No kennel sizes configured.
        </Alert>
      ) : (
        <StyledTableContainer sx={{ mb: 3 }}>
          <Table>
            <StyledTableHead>
              <TableRow>
                <StyledTableHeaderCell>Name</StyledTableHeaderCell>
                <StyledTableHeaderCell>Order</StyledTableHeaderCell>
                <StyledTableHeaderCell align="center">
                  Refs
                </StyledTableHeaderCell>
                <StyledTableHeaderCell align="right">
                  Actions
                </StyledTableHeaderCell>
              </TableRow>
            </StyledTableHead>
            <StyledTableBody>
              {kennelSizes.map((ks, index) => {
                const refCount = usage.sizes?.[ks.id] ?? 0;
                return (
                  <StyledTableRow key={ks.id} isOdd={index % 2 === 0}>
                    <StyledTableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography>{ks.name}</Typography>
                        {ks.isSystem && <SystemChip />}
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography>{ks.sortOrder}</Typography>
                    </StyledTableCell>
                    <StyledTableChipCell>
                      <StyledTableChipCellContent>
                        <RefCountChip count={refCount} />
                      </StyledTableChipCellContent>
                    </StyledTableChipCell>
                    <StyledTableActionsCell align="right">
                      <SettingsTableActions
                        itemId={ks.id}
                        itemName={ks.name}
                        refCount={refCount}
                        isSystem={ks.isSystem}
                        onEdit={() => openSizeDialog(ks)}
                        onDelete={() =>
                          setDeleteTarget({
                            type: 'size',
                            id: ks.id,
                            name: ks.name,
                          })
                        }
                        refLabel="kennel"
                      />
                    </StyledTableActionsCell>
                  </StyledTableRow>
                );
              })}
            </StyledTableBody>
          </Table>
        </StyledTableContainer>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Kennel Sides & Kennels Section */}
      <SettingsHeaderRow
        title="Manage Kennels"
        onAdd={() => openSideDialog()}
        addLabel="Add Kennel Side"
        buttonMinWidth="320px"
      />

      {sides.length === 0 ? (
        <Alert severity="info">No kennel sides configured.</Alert>
      ) : (
        getSortedSides().map((side, index) => {
          const sideKennels = kennels.filter((k) => k.kennelSideId === side.id);
          const sideHasKennels = side.hasKennels !== false;
          const sideRefCount = usage.sides[side.id] ?? 0;
          return (
            <StyledAccordion
              key={side.id}
              expanded={expandedSide === side.id}
              onChange={handleAccordionChange(side.id)}
            >
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                isOdd={index % 2 === 0}
                sx={{ padding: '0 4px 0 12px' }}
              >
                <AccordionButtonLabel
                  display="flex"
                  alignItems="center"
                  gap={2}
                  flex={1}
                  sx={{ minWidth: 0 }}
                >
                  <Typography
                    fontWeight={700}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flexShrink: 1,
                      minWidth: 0,
                      color: '#000',
                      '@media (max-width: 600px)': {
                        maxWidth: '14rem',
                      },
                    }}
                  >
                    {side.name}
                  </Typography>
                  {side.isSystem && <SystemChip />}
                  <Box flex={1} />
                  <SettingsTableActions
                    itemId={side.id}
                    itemName={side.name}
                    refCount={sideRefCount}
                    isSystem={side.isSystem}
                    onEdit={() => openSideDialog(side)}
                    onDelete={() =>
                      setDeleteTarget({
                        type: 'side',
                        id: side.id,
                        name: side.name,
                      })
                    }
                    refLabel="kennel"
                    stopPropagation={true}
                    editColor={ACCORDION_ICON_COLORS.edit}
                    deleteColor={ACCORDION_ICON_COLORS.delete}
                    disabledDeleteColor={ACCORDION_ICON_COLORS.disabledDelete}
                  />
                </AccordionButtonLabel>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <Box display="flex" alignItems="center" mb={1} px={2}>
                  <KennelCountChip count={sideKennels.length} />
                  <Box flex={1} />
                  {sideHasKennels && (
                    <SettingsActionButton
                      startIcon={<AddIcon />}
                      bgColor={theme.palette.primary.main}
                      textColor={theme.palette.grey[0] || '#fff'}
                      hoverBgColor={theme.palette.primary.dark}
                      hoverTextColor="#fff"
                      onClick={() => openKennelDialog(side.id)}
                      sx={{ width: '220px' }}
                    >
                      Add Kennel
                    </SettingsActionButton>
                  )}
                </Box>
                {sideKennels.length === 0 ? (
                  <Box px={2}>
                    <Typography variant="body2" color="textSecondary">
                      {sideHasKennels
                        ? 'No kennels on this side.'
                        : 'This side has no individual kennels.'}
                    </Typography>
                  </Box>
                ) : (
                  <StyledTableContainer>
                    <Table>
                      <StyledTableHead>
                        <TableRow>
                          <StyledTableHeaderCell>#</StyledTableHeaderCell>
                          <StyledTableHeaderCell>Size</StyledTableHeaderCell>
                          <StyledTableHeaderCell align="center">
                            Refs
                          </StyledTableHeaderCell>
                          <StyledTableHeaderCell align="right">
                            Actions
                          </StyledTableHeaderCell>
                        </TableRow>
                      </StyledTableHead>
                      <StyledTableBody>
                        {sideKennels
                          .sort((a, b) => a.kennelNumber - b.kennelNumber)
                          .map((k, index) => {
                            const refCount = usage.kennels[k.id] ?? 0;
                            return (
                              <StyledTableRow
                                key={k.id}
                                isOdd={index % 2 === 0}
                              >
                                <StyledTableCell>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <Typography>{k.kennelNumber}</Typography>
                                    {k.isSystem && <SystemChip />}
                                  </Box>
                                </StyledTableCell>
                                <StyledTableCell>
                                  <Typography>{k.size}</Typography>
                                </StyledTableCell>
                                <StyledTableChipCell>
                                  <StyledTableChipCellContent>
                                    <RefCountChip count={refCount} />
                                  </StyledTableChipCellContent>
                                </StyledTableChipCell>
                                <StyledTableActionsCell align="right">
                                  <SettingsTableActions
                                    itemId={k.id}
                                    itemName={`Kennel #${k.kennelNumber}`}
                                    refCount={refCount}
                                    isSystem={k.isSystem}
                                    onEdit={() => openKennelDialog(side.id, k)}
                                    onDelete={() =>
                                      setDeleteTarget({
                                        type: 'kennel',
                                        id: k.id,
                                        name: `Kennel #${k.kennelNumber}`,
                                      })
                                    }
                                    refLabel="booking"
                                  />
                                </StyledTableActionsCell>
                              </StyledTableRow>
                            );
                          })}
                      </StyledTableBody>
                    </Table>
                  </StyledTableContainer>
                )}
              </StyledAccordionDetails>
            </StyledAccordion>
          );
        })
      )}

      <KennelSizeDialog
        open={sizeDialogOpen}
        onClose={() => setSizeDialogOpen(false)}
        onSave={saveSize}
        editing={editingSize}
      />

      <KennelSideDialog
        open={sideDialogOpen}
        onClose={() => setSideDialogOpen(false)}
        onSave={saveSide}
        editing={editingSide}
        petTypes={petTypes}
      />

      <KennelDialog
        open={kennelDialogOpen}
        onClose={() => setKennelDialogOpen(false)}
        onSave={saveKennel}
        editing={editingKennel}
        sideId={activeSideId}
        kennelSizes={kennelSizes}
      />

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        title={`Delete ${deleteTarget?.type === 'side' ? 'side' : deleteTarget?.type === 'size' ? 'kennel size' : 'kennel'}?`}
        message={`Are you sure you want to delete "${deleteTarget?.name}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
};

export default KennelsSettings;
